import React, { Component, ErrorInfo, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { styled } from "@mui/material/styles";

const BREAK_WORD = "break-word";

const MarkdownContainer = styled("div")({
  overflowWrap: BREAK_WORD,
  wordBreak: BREAK_WORD,
  overflow: "hidden",
  "& p": {
    margin: "0 0 8px 0",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  "& pre": {
    margin: "8px 0",
    borderRadius: 6,
    overflow: "auto",
  },
  "& code": {
    fontFamily: "'Fira Code', 'Consolas', monospace",
    fontSize: 13,
  },
  "& code:not([class*='language-'])": {
    background: "rgba(0, 0, 0, 0.1)",
    padding: "2px 6px",
    borderRadius: 4,
    color: '#000000',
  },
  "& ul, & ol": {
    margin: "8px 0",
    paddingLeft: 20,
  },
  "& li": {
    marginBottom: 4,
  },
  "& blockquote": {
    margin: "8px 0",
    paddingLeft: 12,
    borderLeft: "3px solid #888",
    color: "#555",
  },
  "& .table-wrapper": {
    overflowX: "auto",
    margin: "8px 0",
  },
  "& table": {
    borderCollapse: "collapse",
    fontSize: 13,
    width: "100%",
  },
  "& th, & td": {
    border: "1px solid #ddd",
    padding: "6px 10px",
    textAlign: "left",
  },
  "& th": {
    background: "#f5f5f5",
    fontWeight: 600,
  },
  "& tr:nth-of-type(even)": {
    background: "#fafafa",
  },
  "& a": {
    color: "#1976d2",
    textDecoration: "none",
    wordBreak: "break-all",
    overflowWrap: BREAK_WORD,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    margin: "12px 0 8px 0",
    fontWeight: 600,
  },
  "& h1": { fontSize: 18 },
  "& h2": { fontSize: 16 },
  "& h3": { fontSize: 15 },
  "& hr": {
    border: "none",
    borderTop: "1px solid #ddd",
    margin: "12px 0",
  },
});

const CodeBlockWrapper = styled("div")({
  position: "relative",
  margin: "8px 0",
  borderRadius: 8,
  overflow: "hidden",
  border: "1px solid #333",
});

const CodeBlockHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "6px 12px",
  background: "#2d2d2d",
  borderBottom: "1px solid #404040",
});

const CodeLanguageLabel = styled("span")({
  display: "flex",
  alignItems: "center",
  gap: 6,
  color: "#ccc",
  fontSize: 13,
  fontWeight: 500,
  textTransform: "capitalize",
});

const CopyButton = styled("button")({
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "none",
  border: "none",
  color: "#aaa",
  fontSize: 12,
  padding: "2px 6px",
  cursor: "pointer",
  borderRadius: 4,
  transition: "color 0.2s, background 0.2s",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.1)",
    color: "#fff",
  },
});

interface MarkdownRendererProps {
  content: string;
}

// Error boundary to gracefully fall back to plain text if rendering fails
class MarkdownErrorBoundary extends Component<
  { children: React.ReactNode; fallbackText: string },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallbackText: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[MarkdownRenderer] Rendering failed, falling back to plain text:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return <span style={{ whiteSpace: "pre-wrap" }}>{this.props.fallbackText}</span>;
    }
    return this.props.children;
  }
}

// Known language identifiers for detecting un-fenced code blocks
const KNOWN_LANGUAGES = new Set([
  "yaml", "yml", "json", "python", "py", "javascript", "js", "typescript", "ts",
  "bash", "sh", "shell", "sql", "html", "css", "xml", "java", "go", "rust",
  "ruby", "rb", "c", "cpp", "csharp", "cs", "dockerfile", "terraform", "hcl",
  "toml", "ini", "makefile", "groovy", "kotlin", "swift", "scala", "r",
  "powershell", "ps1", "lua", "perl", "php",
]);

/**
 * Preprocess content to detect un-fenced code blocks and fix
 * fenced blocks where the language name is on a separate line.
 */
const preprocessContent = (content: string): string => {
  // Fix fenced code blocks where the language is on the next line:
  //   ```\nyaml\n...  →  ```yaml\n...
  const processed = content.replaceAll(
    /```\s*\n((?:yaml|yml|json|python|py|javascript|js|typescript|ts|bash|sh|shell|sql|html|css|xml|java|go|rust|ruby|rb|c|cpp|csharp|cs|dockerfile|terraform|hcl|toml|ini|makefile|groovy|kotlin|swift|scala|r|powershell|ps1|lua|perl|php))\n/gi,
    (_, lang) => "```" + lang.trim().toLowerCase() + "\n"
  );

  // If already fenced after the fix above, return
  if (processed.includes("```")) { return processed; }

  // Otherwise, detect un-fenced code blocks (language name on its own line)
  const lines = processed.split("\n");
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const trimmed = lines[i].trim().toLowerCase();

    if (KNOWN_LANGUAGES.has(trimmed) && i + 1 < lines.length) {
      const lang = trimmed;
      const codeLines: string[] = [];
      i++;
      while (i < lines.length) {
        codeLines.push(lines[i]);
        i++;
      }
      result.push("```" + lang);
      result.push(...codeLines);
      result.push("```");
    } else {
      result.push(lines[i]);
      i++;
    }
  }

  return result.join("\n");
};

const CodeBlock: React.FC<{ code: string; language: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [code]);

  return (
    <CodeBlockWrapper>
      <CodeBlockHeader>
        <CodeLanguageLabel>
          <span style={{ fontSize: 14 }}>&lt;/&gt;</span>
          {language === "text" ? "Code" : language}
        </CodeLanguageLabel>
        <CopyButton onClick={handleCopy}>
          {copied ? (
            <>&#10003; Copied</>
          ) : (
            <>&#9112; Copy</>
          )}
        </CopyButton>
      </CodeBlockHeader>
      <SyntaxHighlighter
        style={oneDark}
        language={language}
        PreTag="div"
        customStyle={{ margin: 0, borderRadius: 0 }}
      >
        {code}
      </SyntaxHighlighter>
    </CodeBlockWrapper>
  );
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  const processedContent = preprocessContent(content);
  return (
    <MarkdownErrorBoundary fallbackText={content}>
      <MarkdownContainer>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Open all links in new tab
          a: MarkdownLink,
          pre({ children }) {
            // Pass through children directly so SyntaxHighlighter
            // controls its own wrapper (avoids <pre><div> nesting)
            return <>{children}</>;
          },
          code({ node, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            const codeString = String(children).replace(/\n$/, "");
            // Detect block code: has a language tag OR is multiline OR parent is <pre>
            const isBlockCode =
              !!match ||
              codeString.includes("\n") ||
              (node?.position && node?.data?.meta !== undefined);

            if (isBlockCode) {
              return (
                <CodeBlock code={codeString} language={match ? match[1] : "text"} />
              );
            }

            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          table({ children }) {
            return (
              <div className="table-wrapper">
                <table>{children}</table>
              </div>
            );
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </MarkdownContainer>
    </MarkdownErrorBoundary>
  );
};

function MarkdownLink({ node: _node, href, children, ...props }: any) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
}

export default MarkdownRenderer;
